import type { CreateChatActionPill } from "@/types/chat"
import type {
  CreateTranscriptMessageArgs,
  CreateTranscriptMessageRequest,
  CreateTranscriptMessageResponse,
  RunToolArgs,
  RunToolRequest,
  RunToolResponse,
  VoiceHistoryItem,
  VoiceOption,
  VoiceToolResult,
  VoiceTranscriptPersistenceResult,
} from "@/types/voice-chat"
import type { VoiceInterviewCompletion } from "@/types/voice-interview"

import {
  mapRunToolResponse,
  mapVoiceTranscriptPersistenceResponse,
} from "@/lib/storage/api/voice-chat-api"
import type { VoiceInterviewFlow, VoicePreviewResponse } from "@/lib/voice-api"
import { baseApi } from "./base-api"

export interface CreateOpenAIVoiceInterviewSessionArgs {
  chatId: number
  sdpOffer: string
  voice: VoiceOption
  flow: VoiceInterviewFlow
  voiceInterviewSessionId?: string | null
}

interface CreateOpenAIVoiceInterviewSessionRequest {
  chat_id: number
  sdp_offer: string
  voice: VoiceOption
  flow: VoiceInterviewFlow
  voice_interview_session_id?: string
}

interface CreateOpenAIVoiceInterviewSessionResponse {
  sdp_answer: string
  openai_call_id: string
  voice_interview_session_id: string
  provider: "openai"
  flow: VoiceInterviewFlow
  voice: VoiceOption
  prompt_version: string
  expires_in_seconds: number
  history_items: VoiceHistoryItem[]
}

export interface OpenAIVoiceInterviewSession {
  sdpAnswer: string
  openaiCallId: string
  voiceInterviewSessionId: string
  provider: "openai"
  flow: VoiceInterviewFlow
  voice: VoiceOption
  promptVersion: string
  expiresInSeconds: number
  historyItems: VoiceHistoryItem[]
}

export type PersistOpenAIVoiceInterviewTranscriptArgs =
  CreateTranscriptMessageArgs & {
    voiceInterviewSessionId: string
  }

type PersistOpenAIVoiceInterviewTranscriptRequest =
  CreateTranscriptMessageRequest & {
    voice_interview_session_id: string
  }

export type RunOpenAIVoiceInterviewToolArgs = RunToolArgs & {
  voiceInterviewSessionId: string
}

type RunOpenAIVoiceInterviewToolRequest = Omit<
  RunToolRequest,
  "tool_name" | "arguments"
> & {
  voice_interview_session_id: string
}

interface OpenAISetupVoiceInterviewCompletionResponse {
  flow: "setup"
  enqueued: boolean
  next_steps: CreateChatActionPill[]
}

export interface FinalizeOpenAIVoiceInterviewResponse {
  completed: true
}

export const voiceInterviewApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createOpenAISession: builder.mutation<
      OpenAIVoiceInterviewSession,
      CreateOpenAIVoiceInterviewSessionArgs
    >({
      query: ({ chatId, sdpOffer, voice, flow, voiceInterviewSessionId }) => {
        const data: CreateOpenAIVoiceInterviewSessionRequest = {
          chat_id: chatId,
          sdp_offer: sdpOffer,
          voice,
          flow,
          ...(voiceInterviewSessionId
            ? { voice_interview_session_id: voiceInterviewSessionId }
            : {}),
        }
        return {
          url: "/api/voice_interview_sessions/openai/sessions",
          method: "POST",
          data,
        }
      },
      transformResponse: (
        response: CreateOpenAIVoiceInterviewSessionResponse
      ): OpenAIVoiceInterviewSession => ({
        sdpAnswer: response.sdp_answer,
        openaiCallId: response.openai_call_id,
        voiceInterviewSessionId: response.voice_interview_session_id,
        provider: response.provider,
        flow: response.flow,
        voice: response.voice,
        promptVersion: response.prompt_version,
        expiresInSeconds: response.expires_in_seconds,
        historyItems: response.history_items,
      }),
    }),
    persistOpenAITranscript: builder.mutation<
      VoiceTranscriptPersistenceResult,
      PersistOpenAIVoiceInterviewTranscriptArgs
    >({
      query: ({
        chatId,
        role,
        transcript,
        providerEventId,
        providerEventIds,
        toolCallIds,
        openaiCallId,
        interrupted,
        fullTranscript,
        truncatedAtMs,
        cutoffReason,
        voiceInterviewSessionId,
      }) => {
        const data: PersistOpenAIVoiceInterviewTranscriptRequest = {
          chat_id: chatId,
          role,
          transcript,
          provider_event_id: providerEventId,
          provider_event_ids: providerEventIds,
          tool_call_ids: toolCallIds,
          openai_call_id: openaiCallId,
          interrupted,
          full_transcript: fullTranscript,
          truncated_at_ms: truncatedAtMs,
          cutoff_reason: cutoffReason,
          voice_interview_session_id: voiceInterviewSessionId,
        }
        return {
          url: "/api/voice_interview_sessions/transcript_messages",
          method: "POST",
          data,
        }
      },
      transformResponse: (
        response: CreateTranscriptMessageResponse
      ): VoiceTranscriptPersistenceResult =>
        mapVoiceTranscriptPersistenceResponse(response),
    }),
    runOpenAITool: builder.mutation<
      VoiceToolResult,
      RunOpenAIVoiceInterviewToolArgs
    >({
      query: (args) => {
        const data: RunOpenAIVoiceInterviewToolRequest = {
          chat_id: args.chatId,
          openai_call_id: args.openaiCallId,
          user_message_id: args.userMessageId,
          call_id: args.callId,
          voice_interview_session_id: args.voiceInterviewSessionId,
        }
        return {
          url: "/api/voice_interview_sessions/tool_calls",
          method: "POST",
          data,
        }
      },
      transformResponse: (response: RunToolResponse): VoiceToolResult =>
        mapRunToolResponse(response),
    }),
    prepareOpenAICompletion: builder.mutation<
      VoiceInterviewCompletion,
      { voiceInterviewSessionId: string; finishInterrupted?: boolean }
    >({
      query: ({ voiceInterviewSessionId, finishInterrupted }) => ({
        url: "/api/voice_interview_sessions/prepare_completion",
        method: "POST",
        data: {
          voice_interview_session_id: voiceInterviewSessionId,
          ...(finishInterrupted ? { finish_interrupted: true } : {}),
        },
      }),
      transformResponse: (
        response: OpenAISetupVoiceInterviewCompletionResponse
      ): VoiceInterviewCompletion => ({
        flow: response.flow,
        enqueued: response.enqueued,
        nextSteps: response.next_steps,
      }),
    }),
    finalizeOpenAIInterview: builder.mutation<
      FinalizeOpenAIVoiceInterviewResponse,
      { voiceInterviewSessionId: string }
    >({
      query: ({ voiceInterviewSessionId }) => ({
        url: "/api/voice_interview_sessions/finalize",
        method: "POST",
        data: { voice_interview_session_id: voiceInterviewSessionId },
      }),
    }),
    previewOpenAISetupVoice: builder.mutation<
      VoicePreviewResponse,
      { voice: VoiceOption }
    >({
      query: ({ voice }) => ({
        url: "/api/voice_interview_sessions/openai/preview",
        method: "POST",
        data: { voice },
      }),
    }),
  }),
})

export const { usePreviewOpenAISetupVoiceMutation } = voiceInterviewApi
